import { Grid, useTheme } from "@mui/material";
import {
  getHistoryCrypto,
  getHistoryCryptoList,
  getHistoryFiat,
  getHistoryFiatList,
} from "store/slices/wallet/walletSlice";
import { useEffect, useMemo, useState } from "react";

import Address from "./Address";
import Amount from "./Amount";
import Date from "./Date";
import { LoadingButton } from "@mui/lab";
import MobileWithdrawOrDeposit from "../MobileWithdrawOrDeposit";
import Pagination from "components/Pagination";
import { EmptyStateIcon, PreviewOpen } from "assets/Icon";
import Side from "./Side";
import State from "./State";
import Table from "components/Table";
import TableSkeleton from "components/Skeletons/Table.skeleton";
import TxId from "./TxId";
import WithdrawOrDepositDetailModal from "../WithdrawOrDepositDetailModal";
import { formatDateToJalali } from "utils/helpers/time";
import useDispatch from "hooks/useDispatch";
import useIsMobileView from "hooks/useIsMobileView";
import useNotify from "hooks/useNotify";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import HistoryWithdrawSkeleton from "components/Skeletons/HistoryWithdraw.Skeleton";
import moment from "moment-jalaali";
import EmptyState from "components/EmptyState";

const headers = [
  {
    key: "side",
    name: "",
    align: "left",
  },
  {
    key: "state",
    name: "",
    align: "left",
  },
  {
    key: "amount",
    name: "Amount",
    align: "left",
  },
  {
    key: "network",
    name: "Network",
    align: "left",
  },
  {
    key: "type",
    name: "Type",
    align: "left",
  },
  {
    key: "address",
    name: "Address",
    align: "left",
  },
  {
    key: "txId",
    name: "Tx ID",
    align: "left",
  },
  {
    key: "date",
    name: "Date",
    align: "left",
  },
  {
    key: "action",
    name: "Action",
    align: "right",
  },
];

function WithdrawOrDepositHistoryTable({ isShowJust24h = false }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const notify = useNotify();
  const isMobileView = useIsMobileView();

  const historyList = useSelector((state) => state.wallet.historyList);

  const [openDetails, setOpenDetails] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const coinId = +searchParams.get("coinId") || -1;
  const sideId = +searchParams.get("sideId") || 0;
  const startDate = searchParams.get("startDate") || "";
  const endDate = searchParams.get("endDate") || "";
  const pageNumber = searchParams.get("page") || 1;
  const pageSize = searchParams.get("perPage") || 10;
  const inSideId = searchParams.get("inSideId");

  const isDigital = useMemo(
    () => searchParams.get("isDigital")?.toLocaleLowerCase() !== "false",
    [searchParams]
  );

  const getHistoryOfWithdrawOrDeposit = async ({ pageSize, pageNumber }) => {
    setLoading(true);

    const isLast24h = inSideId && +inSideId === 0

    let body = {
      coinId,
      sideId,
      pageSize,
      pageNumber,
    };

    if (isLast24h || isShowJust24h) {
      const end = moment();
      const start = moment().subtract(24, "hours");

      body.startDate = formatDateToJalali(start.toDate());
      body.endDate = formatDateToJalali(end.toDate());
    } else {
      if (startDate) body.startDate = formatDateToJalali(startDate);
      if (endDate) body.endDate = formatDateToJalali(endDate);
    }



    if (isDigital) {
      await dispatch(getHistoryCryptoList(body));
    } else {
      await dispatch(getHistoryFiatList(body));
    }
    setLoading(false);
  };

  const handleChangePaginate = ({ page, perPage }) => {
    getHistoryOfWithdrawOrDeposit({ pageSize: perPage, pageNumber: page });
  };

  const handleShowDetails = async (data) => {
    setLoadingDetails(true);

    try {
      if (isDigital) {
        await dispatch(getHistoryCrypto(data?.id));
      } else {
        await dispatch(getHistoryFiat(data?.id));
      }

      setOpenDetails(true);
    } catch (e) {
      notify(e?.response?.data?.message, "error");
    }

    setLoadingDetails(false);
  };

  const checkBlinkColor = (data) => {
    const jalaliDateTime = `${data?.date} ${data?.time}`;

    const eventTime = moment(jalaliDateTime, "jYYYY/jMM/jDD HH:mm:ss");
    const now = moment();

    const diffInSeconds = now.diff(eventTime, "seconds");

    return diffInSeconds >= 0 && diffInSeconds <= 120;
  };

  const formattedItems = useMemo(() => {
    return historyList?.data?.map((data) => ({
      ...data,
      state: <State data={data} />,
      txId: <TxId data={data} />,
      address: <Address data={data} />,
      amount: <Amount data={data} />,
      side: <Side data={data} />,
      date: <Date data={data} />,
      network: data?.network || data?.coinName,
      action: (
        <LoadingButton
          loading={loadingDetails}
          onClick={() => handleShowDetails(data)}
        >
          <PreviewOpen color={theme.palette.text.secondary} />
        </LoadingButton>
      ),
      color: checkBlinkColor(data) ? theme.palette.primary.light : 'inherit'
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyList?.data, theme.palette.text.secondary, loadingDetails]);

  useEffect(() => {
    getHistoryOfWithdrawOrDeposit({ pageSize, pageNumber });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coinId, sideId, startDate, endDate, isDigital, inSideId]);

  return (
    <Grid container mt={1}>
      <Grid item xs={12} id={"history-section"}>
        {!loading ? (
          <>
            {isMobileView ? (
              historyList?.data?.length !== 0 ? (
                <>
                  {historyList?.data?.map((data) => (
                    <MobileWithdrawOrDeposit
                      data={data}
                      onClick={handleShowDetails}
                      isRowBlink
                      bg={checkBlinkColor(data) ? theme.palette.primary.light : null}
                    />
                  ))}
                </>
              ) : (
                <EmptyState
                  text={"No Data Available"}
                  icon={<EmptyStateIcon color={theme.palette.modules.border} />}
                />
              )
            ) : (
              <Table headers={headers} items={formattedItems} isRowBlink />
            )}
          </>
        ) : (
          <>{isMobileView ? <HistoryWithdrawSkeleton /> : <TableSkeleton />}</>
        )}
      </Grid>

      <Grid item xs={12}>
        <Pagination
          perPage={historyList?.pageSize}
          count={historyList?.totalPages}
          onChange={handleChangePaginate}
        />
      </Grid>

      <WithdrawOrDepositDetailModal
        open={openDetails}
        onClose={() => {
          setOpenDetails(false);
        }}
      />
    </Grid>
  );
}

export default WithdrawOrDepositHistoryTable;
